import { Loading, TextInput } from "@vellum/ui";
import type { InternalFacilitySchema } from "@vellum/vellum-api";
import { useEffect, useMemo, useState, type ChangeEvent } from "react";

import { vellumApiSdk } from "@vellum/micro-app-auth";
import { ChevronRightIcon } from "@/components/ChevronRightIcon";

type FacilitySelectProps = {
  companyId: string;
  onSelect: (facility: InternalFacilitySchema) => void;
};

/** Searchable list of a company's facilities (with city/state). Filters locally by name. */
export function FacilitySelect({ companyId, onSelect }: FacilitySelectProps) {
  const [facilities, setFacilities] = useState<InternalFacilitySchema[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    vellumApiSdk
      .listFacilities({ query: { company_id: companyId } })
      .then((res) => {
        if (active) setFacilities(res.data ?? []);
      })
      .catch(() => {
        if (active) setError("Something went wrong loading facilities.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [companyId]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = q
      ? facilities.filter((f) => f.name.toLowerCase().includes(q))
      : facilities;
    return [...list].sort((a, b) => a.name.localeCompare(b.name));
  }, [facilities, query]);

  return (
    <div className="flex min-h-0 w-full flex-1 flex-col gap-3 p-4">
      <TextInput
        id="facility-search"
        placeholder="Search facilities"
        value={query}
        onChange={(e: ChangeEvent<HTMLInputElement>) =>
          setQuery(e.target.value)
        }
      />
      {loading ? (
        <div className="flex flex-1 items-center justify-center">
          <Loading />
        </div>
      ) : error ? (
        <p className="py-10 text-center text-sm text-error">{error}</p>
      ) : filtered.length === 0 ? (
        <p className="py-10 text-center text-sm text-gray-500">
          No facilities found for this company.
        </p>
      ) : (
        <ul className="flex flex-1 flex-col divide-y divide-gray-200 overflow-y-auto rounded-md border border-gray-200 bg-white">
          {filtered.map((facility) => {
            const location = [facility.city, facility.state]
              .filter(Boolean)
              .join(", ");
            return (
              <li key={facility.id}>
                <button
                  type="button"
                  onClick={() => onSelect(facility)}
                  className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-gray-100"
                >
                  <span className="flex flex-col">
                    <span className="text-sm font-medium text-gray-900">
                      {facility.name}
                    </span>
                    {location ? (
                      <span className="text-xs text-gray-500">{location}</span>
                    ) : null}
                  </span>
                  <ChevronRightIcon className="h-4 w-4 shrink-0 text-gray-400" />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
